import React, { useState, useEffect } from 'react';
import api from '../../services/api';

const FIELD_LABELS = {
  title:            'Titel',
  ingredient_amount:'Zutat – Menge',
  ingredient_unit:  'Zutat – Einheit',
  ingredient_name:  'Zutat – Name',
  step:             'Zubereitungsschritt',
};

const FIELD_COLORS = {
  title:            'var(--admin-field-title)',
  ingredient_amount:'var(--admin-field-amount)',
  ingredient_unit:  'var(--admin-field-unit)',
  ingredient_name:  'var(--admin-field-name)',
  step:             'var(--admin-field-step)',
};

const C = {
  bg: 'var(--admin-bg-input)', card: 'var(--admin-bg)', border: 'var(--admin-border)',
  text: 'var(--admin-text)', muted: 'var(--admin-text-dim)', accent: 'var(--admin-btn-primary)',
};

function StatBadge({ label, value, color }) {
  return (
    <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, padding: '14px 20px', minWidth: 120, textAlign: 'center' }}>
      <div style={{ fontSize: '1.6rem', fontWeight: 800, color: color || C.accent }}>{value}</div>
      <div style={{ fontSize: '.72rem', color: C.muted, marginTop: 3 }}>{label}</div>
    </div>
  );
}

const INTERVAL_OPTIONS = [
  { value: 24,  label: 'Täglich (24h)' },
  { value: 48,  label: 'Alle 2 Tage' },
  { value: 168, label: 'Wöchentlich' },
];

export default function Corrections() {
  const [corrections, setCorrections] = useState([]);
  const [stats,       setStats]       = useState(null);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState('');
  const [filter,      setFilter]      = useState('');
  const [analysis,    setAnalysis]    = useState('');
  const [analysisMeta, setAnalysisMeta] = useState(null);
  const [analyzing,   setAnalyzing]   = useState(false);
  const [analyzeErr,  setAnalyzeErr]  = useState('');

  // Scheduler
  const [schedule,       setSchedule]       = useState(null);
  const [scheduleEmail,  setScheduleEmail]  = useState('');
  const [scheduleHours,  setScheduleHours]  = useState(24);
  const [scheduleEnabled,setScheduleEnabled]= useState(false);
  const [scheduleSaving, setScheduleSaving] = useState(false);
  const [scheduleSaved,  setScheduleSaved]  = useState(false);
  const [scheduleError,  setScheduleError]  = useState('');
  const [exportError,    setExportError]    = useState('');

  useEffect(() => { load(); loadSchedule(); }, []);

  async function load() {
    setLoading(true); setError('');
    try {
      const d = await api.getCorrections(500);
      setCorrections(d.corrections || []);
      setStats(d.stats || null);
    } catch (e) {
      setError('Fehler beim Laden: ' + (e.message || 'Unbekannt'));
    } finally {
      setLoading(false);
    }
  }

  async function loadSchedule() {
    try {
      const d = await api.getSchedule();
      const s = d.schedule || {};
      setSchedule(s);
      setScheduleEmail(s.notify_email  || '');
      setScheduleHours(s.interval_hours || 24);
      setScheduleEnabled(!!s.enabled);
    } catch (e) { /* silent */ }
  }

  async function handleAnalyze(force = false) {
    setAnalyzing(true); setAnalyzeErr(''); setAnalysis(''); setAnalysisMeta(null);
    try {
      const d = await api.analyzeCorrections(force);
      setAnalysis(d.analysis);
      setAnalysisMeta({
        cached:    d.cached,
        count:     d.corrections_analyzed,
        lastRun:   d.last_run,
        emailSent: d.email_sent,
        emailErr:  d.email_error,
      });
    } catch (e) {
      setAnalyzeErr('Analyse fehlgeschlagen: ' + (e.message || 'Unbekannt'));
    } finally {
      setAnalyzing(false);
    }
  }

  async function handleSaveSchedule() {
    setScheduleSaving(true); setScheduleSaved(false); setScheduleError('');
    try {
      const d = await api.saveSchedule({
        interval_hours: scheduleHours,
        notify_email:   scheduleEmail,
        enabled:        scheduleEnabled,
      });
      setSchedule(d.schedule);
      setScheduleSaved(true);
      setTimeout(() => setScheduleSaved(false), 2500);
    } catch (e) {
      setScheduleError('Fehler: ' + e.message);
    } finally {
      setScheduleSaving(false);
    }
  }

  function handleExport() {
    setExportError('');
    api.exportCorrections().then(d => {
      const blob = new Blob([JSON.stringify(d, null, 2)], { type: 'application/json' });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = `ocr-corrections-${new Date().toISOString().slice(0,10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
    }).catch(e => setExportError('Export fehlgeschlagen: ' + e.message));
  }

  const filtered = filter
    ? corrections.filter(c => c.field === filter)
    : corrections;

  return (
    <div style={{ padding: '24px 28px', fontFamily: 'Inter,sans-serif', maxWidth: 1200 }}>

      {/* Kopfzeile */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: '1.3rem', color: C.text, fontWeight: 600, margin: 0 }}>🔬 OCR-Korrekturen</h1>
          <p style={{ color: C.muted, fontSize: '.8rem', margin: '4px 0 0' }}>
            Manuelle Korrekturen der Benutzer an gescannten Rezepten — Grundlage für Prompt-Optimierung
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <div>
            <button
              onClick={handleExport}
              style={{ background: 'none', border: `1px solid ${C.border}`, color: C.text, borderRadius: 6, padding: '7px 16px', fontSize: '.78rem', cursor: 'pointer' }}
            >
              ⬇ JSON Export
            </button>
            {exportError && <div style={{ color: 'var(--admin-danger)', fontSize: '.72rem', marginTop: 4 }}>{exportError}</div>}
          </div>
          <button
            onClick={() => handleAnalyze(false)}
            disabled={analyzing || corrections.length === 0}
            style={{ background: analyzing ? 'color-mix(in srgb, var(--admin-btn-primary) 50%, transparent)' : C.accent, border: 'none', color: '#fff', borderRadius: 6, padding: '7px 18px', fontSize: '.78rem', fontWeight: 600, cursor: 'pointer', opacity: corrections.length === 0 ? 0.5 : 1 }}
          >
            {analyzing ? '⏳ Analysiert…' : '🤖 Analysieren'}
          </button>
          <button
            onClick={() => handleAnalyze(true)}
            disabled={analyzing || corrections.length === 0}
            style={{ background: 'none', border: `1px solid ${C.accent}`, color: C.accent, borderRadius: 6, padding: '7px 14px', fontSize: '.78rem', fontWeight: 600, cursor: 'pointer', opacity: corrections.length === 0 ? 0.5 : 1 }}
            title="Cache ignorieren und neu analysieren"
          >
            🔄 Neu
          </button>
        </div>
      </div>

      {error && (
        <div style={{ background: 'color-mix(in srgb, var(--admin-danger) 10%, transparent)', border: '1px solid var(--admin-danger)', borderRadius: 6, padding: '10px 14px', color: 'var(--admin-danger)', fontSize: '.78rem', marginBottom: 16 }}>
          {error} <button onClick={load} style={{ marginLeft: 8, color: 'var(--admin-danger)', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>Neu laden</button>
        </div>
      )}

      {/* Statistiken */}
      {stats && (
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 24 }}>
          <StatBadge label="Korrekturen gesamt" value={stats.total} color="var(--admin-field-name)" />
          {stats.by_field.map(f => (
            <StatBadge
              key={f.field}
              label={FIELD_LABELS[f.field] || f.field}
              value={f.count}
              color={FIELD_COLORS[f.field] || C.muted}
            />
          ))}
        </div>
      )}

      {/* KI-Analyse */}
      {analyzeErr && (
        <div style={{ background: 'color-mix(in srgb, var(--admin-danger) 10%, transparent)', border: '1px solid var(--admin-danger)', borderRadius: 6, padding: '10px 14px', color: 'var(--admin-danger)', fontSize: '.78rem', marginBottom: 16 }}>
          {analyzeErr}
        </div>
      )}
      {/* Scheduler-Einstellungen */}
      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, padding: '16px 20px', marginBottom: 24 }}>
        <div style={{ fontSize: '.75rem', color: C.muted, fontWeight: 700, marginBottom: 12, textTransform: 'uppercase', letterSpacing: '.08em' }}>
          ⏱ Automatische Analyse
        </div>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={scheduleEnabled}
              onChange={e => setScheduleEnabled(e.target.checked)}
              style={{ accentColor: C.accent, width: 16, height: 16 }}
            />
            <span style={{ color: C.text, fontSize: '.82rem', fontWeight: 500 }}>Aktiv</span>
          </label>
          <div>
            <div style={{ fontSize: '.68rem', color: C.muted, marginBottom: 4 }}>Intervall</div>
            <select
              value={scheduleHours}
              onChange={e => setScheduleHours(parseInt(e.target.value))}
              style={{ background: 'var(--admin-bg-input)', border: `1px solid ${C.border}`, color: C.text, borderRadius: 5, padding: '6px 10px', fontSize: '.8rem', cursor: 'pointer' }}
            >
              {INTERVAL_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
          <div style={{ flex: 1, minWidth: 200 }}>
            <div style={{ fontSize: '.68rem', color: C.muted, marginBottom: 4 }}>E-Mail</div>
            <input
              type="email"
              value={scheduleEmail}
              onChange={e => setScheduleEmail(e.target.value)}
              placeholder="deine@email.com"
              style={{ width: '100%', background: 'var(--admin-bg-input)', border: `1px solid ${C.border}`, color: C.text, borderRadius: 5, padding: '6px 10px', fontSize: '.8rem', boxSizing: 'border-box', outline: 'none' }}
            />
          </div>
          <button
            onClick={handleSaveSchedule}
            disabled={scheduleSaving}
            style={{ background: scheduleSaved ? 'var(--admin-saved)' : C.accent, border: 'none', color: '#fff', borderRadius: 6, padding: '7px 18px', fontSize: '.78rem', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}
          >
            {scheduleSaving ? 'Speichert…' : scheduleSaved ? '✓ Gespeichert' : '💾 Speichern'}
          </button>
        </div>
        {scheduleError && (
          <div style={{ color: 'var(--admin-danger)', fontSize: '.72rem', marginTop: 8 }}>{scheduleError}</div>
        )}
        {schedule?.last_run_at && (
          <div style={{ marginTop: 10, fontSize: '.7rem', color: C.muted }}>
            Letzte Analyse: {new Date(schedule.last_run_at).toLocaleString('de-AT')}
            {schedule.next_run_at && ` · Nächste: ${new Date(schedule.next_run_at).toLocaleString('de-AT')}`}
          </div>
        )}
      </div>

      {analysis && (
        <div style={{ background: 'var(--admin-bg-analysis)', border: `1px solid color-mix(in srgb, ${C.accent} 27%, transparent)`, borderRadius: 8, padding: '20px 24px', marginBottom: 24 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
            <div style={{ fontSize: '.75rem', color: C.accent, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em' }}>
              🤖 KI-Analyse — Prompt-Verbesserungsvorschläge
            </div>
            {analysisMeta && (
              <div style={{ fontSize: '.65rem', color: C.muted, textAlign: 'right' }}>
                {analysisMeta.cached ? '📦 Aus Cache' : `✨ Neu (${analysisMeta.count} Korrekturen)`}
                {analysisMeta.emailSent && <span style={{ color: 'var(--admin-success)', marginLeft: 8 }}>✉ Mail gesendet</span>}
                {analysisMeta.emailErr  && <span style={{ color: 'var(--admin-field-step)', marginLeft: 8 }}>✉ Mail fehlgeschlagen</span>}
              </div>
            )}
          </div>
          <pre style={{ color: C.text, fontSize: '.83rem', lineHeight: 1.8, whiteSpace: 'pre-wrap', margin: 0, fontFamily: 'Inter,sans-serif' }}>
            {analysis}
          </pre>
        </div>
      )}

      {/* Filter */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
        {['', ...Object.keys(FIELD_LABELS)].map(f => (
          <button
            key={f || '__all'}
            onClick={() => setFilter(f)}
            style={{
              padding: '4px 12px', borderRadius: 99, fontSize: '.72rem', fontWeight: 600, cursor: 'pointer', border: 'none',
              background: filter === f ? (FIELD_COLORS[f] || C.accent) : C.card,
              color: filter === f ? '#fff' : C.muted,
            }}
          >
            {f ? FIELD_LABELS[f] : 'Alle'}
            {f && stats ? ` (${stats.by_field.find(x => x.field === f)?.count || 0})` : ''}
          </button>
        ))}
      </div>

      {/* Tabelle */}
      {loading ? (
        <div style={{ color: C.muted, padding: 40, textAlign: 'center' }}>Lade…</div>
      ) : filtered.length === 0 ? (
        <div style={{ color: C.muted, padding: 40, textAlign: 'center', background: C.card, borderRadius: 8, border: `1px solid ${C.border}` }}>
          {corrections.length === 0
            ? 'Noch keine Korrekturen vorhanden. Sobald Benutzer gescannte Rezepte bearbeiten, erscheinen sie hier.'
            : 'Keine Korrekturen für diesen Filter.'}
        </div>
      ) : (
        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '.8rem' }}>
            <thead>
              <tr style={{ background: 'var(--admin-bg-dark)', borderBottom: `1px solid ${C.border}` }}>
                {['Feld', 'Original (OCR)', 'Korrigiert', 'Rezept', 'Benutzer', 'Datum'].map(h => (
                  <th key={h} style={{ padding: '10px 14px', textAlign: 'left', color: C.muted, fontWeight: 600, fontSize: '.72rem', textTransform: 'uppercase', letterSpacing: '.05em' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((c, i) => (
                <tr key={c.id} style={{ borderBottom: `1px solid ${C.border}`, background: i % 2 === 0 ? C.card : 'var(--admin-bg-dark)' }}>
                  <td style={{ padding: '9px 14px' }}>
                    <span style={{
                      display: 'inline-block', padding: '2px 8px', borderRadius: 99, fontSize: '.65rem', fontWeight: 700,
                      background: `color-mix(in srgb, ${FIELD_COLORS[c.field] || C.muted} 13%, transparent)`,
                      color: FIELD_COLORS[c.field] || C.muted,
                      border: `1px solid color-mix(in srgb, ${FIELD_COLORS[c.field] || C.muted} 27%, transparent)`,
                    }}>
                      {FIELD_LABELS[c.field] || c.field}
                      {c.field_index !== null && c.field_index !== undefined ? ` #${c.field_index + 1}` : ''}
                    </span>
                  </td>
                  <td style={{ padding: '9px 14px', color: 'var(--admin-field-step)', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {c.original}
                  </td>
                  <td style={{ padding: '9px 14px', color: 'var(--admin-success)', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {c.corrected}
                  </td>
                  <td style={{ padding: '9px 14px', color: C.text, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    <a href={`/edit/${c.recipe_id}`} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--admin-link)', textDecoration: 'none' }}>
                      {c.recipe_title}
                    </a>
                  </td>
                  <td style={{ padding: '9px 14px', color: C.muted }}>{c.username}</td>
                  <td style={{ padding: '9px 14px', color: C.muted, whiteSpace: 'nowrap' }}>
                    {new Date(c.created_at).toLocaleDateString('de-AT', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
